import React from "react";
import { Info, List, Plus, Loader2 } from "lucide-react";
import SearchableSelect from "../SearchableSelect";
import {
  ACTION_CARD,
  ACTION_CARD_FOOTER,
  ACTION_DROPDOWN_RESERVE,
  ACTION_FRAME,
  ACTION_RAIL,
  ACTION_RAIL_ICON_BOX,
  ACTION_RAIL_ICON_SIZE,
  ACTION_RAIL_ITEM,
  ACTION_RAIL_LABEL,
  ACTION_RAIL_TEXT,
  ACTION_SELECT_WELL,
} from "./actionUiChrome";

/** Single guidance tip — use once per form, never on page headers that already explain the action. */
export function ActionInfoTip({ text }) {
  if (!text) return null;
  return (
    <span
      title={text}
      className="inline-flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full border border-[hsl(var(--ds-primary)/0.35)] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)]"
      aria-label={text}
    >
      <Info size={12} strokeWidth={2} className="h-3 w-3" />
    </span>
  );
}

/**
 * Process card: taller body, dropdown opens downward into reserved space; footer CTAs stay visible.
 */
export function ActionProcessCard({
  darkMode = false,
  title,
  infoText = "",
  selectId = "action-employee",
  employeeId,
  onEmployeeChange,
  employeeOptions = null,
  fetchOptions = null,
  loading = false,
  primaryLabel = "Continue",
  onPrimary,
  secondaryLabel = "View list",
  onSecondary = null,
  showSecondary = false,
  children = null,
  emptyMessage = null,
}) {
  const border = "border-[hsl(var(--ds-primary)/0.35)]";
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-start pb-4 pt-1">
      <div className={ACTION_CARD}>
        <div className="relative z-0 flex flex-1 flex-col space-y-4 p-5 pb-3">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
              {title}
            </h3>
            <ActionInfoTip text={infoText} />
          </div>
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-xs text-[hsl(var(--ds-muted-fg))]">
              <Loader2 size={14} className="animate-spin" /> Loading employees…
            </div>
          ) : emptyMessage ? (
            <div className={`rounded-lg border border-dashed px-4 py-10 text-center text-sm ${border}`}>
              {emptyMessage}
            </div>
          ) : (
            <>
              <div className={ACTION_SELECT_WELL}>
                <SearchableSelect
                  id={selectId}
                  label="Employee"
                  value={employeeId}
                  onChange={onEmployeeChange}
                  staticOptions={employeeOptions || undefined}
                  fetchOptions={fetchOptions || undefined}
                  isEmployee={Boolean(fetchOptions) && !employeeOptions}
                  darkMode={darkMode}
                  compact
                  required
                  placeholder="Search code or name…"
                  menuPlacement="bottom"
                  menuPortalTarget={null}
                  inActionCard
                />
              </div>
              <div className={ACTION_DROPDOWN_RESERVE} aria-hidden="true" />
            </>
          )}
          {children}
        </div>
        <div className={ACTION_CARD_FOOTER}>
          {showSecondary && onSecondary ? (
            <button
              type="button"
              onClick={onSecondary}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
            >
              <List size={13} /> {secondaryLabel}
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            disabled={!employeeId || loading || !!emptyMessage}
            onClick={onPrimary}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-4 py-2 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] disabled:opacity-45"
          >
            <Plus size={14} /> {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Left action rail + body — same chrome as Payroll Actions.
 */
export function ModuleActionsFrame({
  actions = [],
  activeKey,
  onSelect,
  icons = {},
  children,
}) {
  return (
    <div className={ACTION_FRAME}>
      <aside className={ACTION_RAIL}>
        <div className="px-2 py-2">
          <p className={ACTION_RAIL_LABEL}>Actions</p>
        </div>
        <nav className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2">
          <ul className="space-y-0.5">
            {actions.map((a) => {
              const Icon3d = icons[a.key];
              const selected = a.key === activeKey;
              return (
                <li key={a.key}>
                  <button
                    type="button"
                    onClick={() => onSelect(a.key)}
                    className={`${ACTION_RAIL_ITEM} ${
                      selected
                        ? "bg-[hsl(var(--ds-primary)/0.1)] ring-1 ring-[hsl(var(--ds-primary)/0.4)]"
                        : "hover:bg-[hsl(var(--ds-muted))]"
                    }`}
                  >
                    <span
                      className={`${ACTION_RAIL_ICON_BOX} ${
                        selected
                          ? "bg-[hsl(var(--ds-primary)/0.08)]"
                          : "bg-transparent"
                      }`}
                    >
                      {Icon3d ? <Icon3d size={ACTION_RAIL_ICON_SIZE} /> : null}
                    </span>
                    <span
                      className={`${ACTION_RAIL_TEXT} ${
                        selected
                          ? "text-[hsl(var(--ds-primary))]"
                          : "text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {a.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--ds-surface))] p-3">
        {children}
      </section>
    </div>
  );
}
